// OptionEditor (job page round 2: the estimate builder): one option of an estimate being built, a
// column of a document. Its letter and tier (good, better, best), recommended or not, its title and
// the line the customer reads under it; then its lines, one a row: the item, quantity × price and the
// amount. A material that goes with a service sits indented under it. A price changed from the price
// book says what it was and why; with no reason yet it asks for one, in red, as the only problem. An
// optional line (the customer ticks it) and a line kept from the customer say so in words. Then its
// links (Add a line, a discount, a fee; copy it), its totals, and for the owner alone the margin line,
// red only below the company's floor. The option being added to is drawn with the heavy rule on top.
// The price book round (DEC-019): a line may carry its picture, and what opens under it (Swap, which
// trades it for its neighbour, in pictures); the option may carry what usually goes with it, after its
// lines. An option with no lines yet says so (the builder opens blank: critic K5). The app's builder
// may put the option's own fields under its head (its title, tier and what it includes), and keep a
// changed price's reason open while it's typed (`editReason`).
// Reads only --opted-*; TextField, Totals, Picture and Link read their own.
import { Children, type ReactNode } from 'react';
import { TextField } from '../Field/Field.tsx';
import { Picture, type PictureSrc, type PictureState } from '../Picture/Picture.tsx';
import { Totals, type TotalsRow } from '../Totals/Totals.tsx';
import './OptionEditor.css';

export interface EditorLine {
  id: string;
  name: string;
  detail?: string;
  qty: string;
  price: string;
  amount: string;
  /** The price book's price, when it was changed: "$3,140.00". */
  wasPrice?: string;
  /** Why it was changed; empty with wasPrice asks for it. */
  reason?: string;
  /** The customer ticks it to have it. */
  isOptional?: boolean;
  /** On the estimate, not shown to the customer. */
  isHidden?: boolean;
  /** A material that comes with the service above it. */
  isPart?: boolean;
  /** Its picture, from the price book; with one, the line draws it first. */
  picture?: PictureSrc | null;
  pictureState?: PictureState;
  /** Under the line: its Swap link, and the choice it opens (PictureChoice). */
  below?: ReactNode;
  /** The reason stays a field (being typed), not the line that says it. */
  editReason?: boolean;
}

export interface OptionEditorProps {
  /** "B" */
  letter: string;
  /** "Better" */
  tier?: string;
  title: string;
  /** What the customer reads under the title. */
  summary?: string;
  isRecommended?: boolean;
  /** The option the price book adds to. */
  isFocused?: boolean;
  lines: EditorLine[];
  totals: TotalsRow[];
  /** The owner's alone: "Margin 41%"; isBad below the floor, with the shortfall. */
  margin?: { text: string; isBad?: boolean };
  onQty?: (lineId: string, qty: string) => void;
  onPrice?: (lineId: string, price: string) => void;
  onReason?: (lineId: string, reason: string) => void;
  /** Its links: Add a line, Add a discount, Add a fee, Copy this option. */
  actions?: ReactNode;
  /** Makes it the option the price book adds to. */
  onFocus?: () => void;
  /** The customer's name, for the hidden line's words ("not shown to Jordan"). */
  customer?: string;
  /** The title's heading level: one under the page's (2 under the builder's h1). */
  level?: 2 | 3;
  /** After its lines: what usually goes with them (PictureChoice, things to add). */
  suggest?: ReactNode;
  /** With no lines yet: "Nothing on A yet." */
  emptyText?: string;
  /** Under its head: the option's own fields (its title, tier, what it includes), where they edit. */
  fields?: ReactNode;
}

export function OptionEditor({
  letter,
  tier,
  title,
  summary,
  isRecommended,
  isFocused,
  lines,
  totals,
  margin,
  onQty,
  onPrice,
  onReason,
  actions,
  onFocus,
  customer = 'the customer',
  level = 3,
  suggest,
  emptyText,
  fields,
}: OptionEditorProps) {
  const name = `Option ${letter}`;
  return (
    <section
      className="fl-opted"
      data-focused={isFocused ? '' : undefined}
      aria-label={`${name}: ${title}`}
      onFocusCapture={onFocus}
    >
      <div className="fl-opted__head">
        <p className="fl-opted__letter">
          {name}
          {tier ? <span className="fl-opted__muted"> · {tier}</span> : null}
          {isRecommended ? <b className="fl-opted__rec"> · recommended</b> : null}
          {isFocused ? <span className="fl-opted__muted"> · adding here</span> : null}
        </p>
        {level === 2 ? (
          <h2 className="fl-opted__title">{title}</h2>
        ) : (
          <h3 className="fl-opted__title">{title}</h3>
        )}
        {summary ? <p className="fl-opted__summary">{summary}</p> : null}
      </div>
      {fields ? <div className="fl-opted__fields">{fields}</div> : null}
      {lines.length ? null : <p className="fl-opted__empty">{emptyText ?? `Nothing on ${letter} yet.`}</p>}
      <ul
        className="fl-opted__lines"
        aria-label={`${name}’s lines`}
        data-empty={lines.length ? undefined : ''}
      >
        {lines.map((l) => (
          <li
            key={l.id}
            className="fl-opted__line"
            data-part={l.isPart ? '' : undefined}
            data-pic={l.picture !== undefined ? '' : undefined}
          >
            {l.picture !== undefined ? (
              <span className="fl-opted__pic">
                <Picture src={l.picture} state={l.pictureState} size="sm" />
              </span>
            ) : null}
            <span className="fl-opted__what">
              <span className="fl-opted__name">{l.name}</span>
              {l.detail ? <span className="fl-opted__muted">{l.detail}</span> : null}
              {l.isOptional ? <span className="fl-opted__muted">optional: {customer} ticks it</span> : null}
              {l.isHidden ? <span className="fl-opted__muted">not shown to {customer}</span> : null}
              {l.wasPrice && l.reason && !l.editReason ? (
                <span className="fl-opted__muted">
                  changed from {l.wasPrice}: {l.reason}
                </span>
              ) : null}
            </span>
            <span className="fl-opted__nums">
              <input
                className="fl-opted__num"
                data-size="qty"
                inputMode="decimal"
                aria-label={`Quantity of ${l.name}`}
                value={l.qty}
                readOnly={!onQty}
                onChange={(e) => onQty?.(l.id, e.target.value)}
              />
              <span aria-hidden="true">×</span>
              <input
                className="fl-opted__num"
                inputMode="decimal"
                aria-label={`Price of ${l.name}`}
                value={l.price}
                readOnly={!onPrice}
                onChange={(e) => onPrice?.(l.id, e.target.value)}
              />
            </span>
            <span className="fl-opted__amount">{l.amount}</span>
            {l.wasPrice && (!l.reason || l.editReason) ? (
              <span className="fl-opted__reason">
                <TextField
                  label={`Why ${l.name} isn’t ${l.wasPrice}`}
                  isRequired
                  error={l.reason?.trim() ? undefined : 'Say why the price changed; the office sees it.'}
                  value={l.reason ?? ''}
                  onChange={(v) => onReason?.(l.id, v)}
                />
              </span>
            ) : null}
            {l.below ? <div className="fl-opted__below">{l.below}</div> : null}
          </li>
        ))}
      </ul>
      {suggest ? <div className="fl-opted__suggest">{suggest}</div> : null}
      {actions ? <div className="fl-opted__acts">{actions}</div> : null}
      <Totals label={`${name}’s totals`} rows={totals} />
      {margin ? (
        <p className="fl-opted__margin" data-bad={margin.isBad ? '' : undefined}>
          Only you see this · {margin.text}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The options: up to three side by side, four to six one under another (domain estimates rule: never
 * seven). One under another on a phone.
 */
export function OptionColumns({ children, label }: { children: ReactNode; label: string }) {
  const n = Children.count(children);
  return (
    <div className="fl-opted-cols" role="group" aria-label={label} data-many={n > 3 ? '' : undefined}>
      {children}
    </div>
  );
}
